// src/components/auth/CompleteInvitationSetup.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { completeInvitationSetup, checkPasswordStrength } from '../../services/api';
import { 
  LockClosedIcon, 
  ExclamationTriangleIcon, 
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import Footer from '../Footer';

function CompleteInvitationSetup() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [isCheckingStrength, setIsCheckingStrength] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isTokenExpired, setIsTokenExpired] = useState(false); // NEW: Track expired token
  
  const token = searchParams.get('token');

  // Set language from institution on mount
  useEffect(() => {
    const institutionData = localStorage.getItem('selectedInstitution');
    if (institutionData) {
      try {
        const institution = JSON.parse(institutionData);
        const publicLanguage = institution.public_portal_language || 'en';
        console.log('[Complete Invitation] Setting language:', publicLanguage);
        i18n.changeLanguage(publicLanguage).catch(error => {
          console.error('[Complete Invitation] Failed to set language, falling back to English:', error);
          i18n.changeLanguage('en');
        });
      } catch (error) {
        console.error('[Complete Invitation] Failed to parse institution data:', error);
        i18n.changeLanguage('en');
      }
    }
  }, [i18n]);

  useEffect(() => {
    document.title = t('auth.invitation.pageTitle');
  }, [t]);

  useEffect(() => {
    if (!token) {
      setError(t('auth.invitation.invalidLink'));
    }
  }, [token, t]);

  useEffect(() => {
    if (!password) {
      setPasswordStrength(null);
      return;
    }

    const timer = setTimeout(async () => {
      setIsCheckingStrength(true);
      try {
        const response = await checkPasswordStrength(password);
        if (response.success) {
          setPasswordStrength(response.data);
        }
      } catch (error) {
        console.error('Error checking password strength:', error);
      } finally {
        setIsCheckingStrength(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [password]);

  const validatePassword = () => {
    const errors = [];
    
    if (!password || password.length < 10) {
      errors.push(t('auth.invitation.validation.minLength'));
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push(t('auth.invitation.validation.lowercase'));
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push(t('auth.invitation.validation.uppercase'));
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push(t('auth.invitation.validation.number'));
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push(t('auth.invitation.validation.special'));
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Check if terms are accepted
    if (!acceptTerms) {
      setError(t('auth.invitation.termsRequired'));
      return;
    }

    const validationErrors = validatePassword();
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. ') + '.');
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.invitation.passwordMismatch'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await completeInvitationSetup(token, password, confirmPassword);
      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        // NEW: Check for expired token error codes
        if (response.errorCode === 'INVALID_OR_EXPIRED_TOKEN' || 
            response.errorCode === 'EXPIRED_INVITATION_TOKEN' ||
            response.message?.toLowerCase().includes('expired') ||
            response.message?.toLowerCase().includes('invalid')) {
          setIsTokenExpired(true);
        } else {
          setError(response.message || t('auth.invitation.errorSetup'));
        }
      }
    } catch (err) {
      // NEW: Check error response for expired token
      const errorCode = err.data?.errorCode || err.errorCode;
      const errorMessage = err.data?.message || err.message || '';
      
      if (errorCode === 'INVALID_OR_EXPIRED_TOKEN' || 
          errorCode === 'EXPIRED_INVITATION_TOKEN' ||
          errorMessage.toLowerCase().includes('expired') ||
          errorMessage.toLowerCase().includes('invalid')) {
        setIsTokenExpired(true);
      } else {
        setError(errorMessage || t('auth.invitation.errorOccurred'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'strong': return 'text-green-600 bg-green-50 border-green-200';
      case 'good': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'fair': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'weak': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStrengthBarColor = (strength) => {
    switch (strength) {
      case 'strong': return 'bg-green-500';
      case 'good': return 'bg-blue-500';
      case 'fair': return 'bg-yellow-500';
      case 'weak': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  const meetsRequirement = (requirement) => {
    if (!password) return false;
    
    switch (requirement) {
      case 'length':
        return password.length >= 10;
      case 'lowercase':
        return /[a-z]/.test(password);
      case 'uppercase':
        return /[A-Z]/.test(password);
      case 'number':
        return /[0-9]/.test(password);
      case 'special':
        return /[!@#$%^&*(),.?":{}|<>]/.test(password);
      default:
        return false;
    }
  };

  // NEW: Show expired token screen
  if (isTokenExpired) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 px-4 py-8">
          <div className="w-full max-w-md text-center">
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg">
              <div className="rounded-full bg-yellow-100 p-3 mx-auto w-fit mb-4">
                <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                {t('auth.invitation.linkExpired') || 'This Link Has Expired'}
              </h2>
              <p className="text-slate-600 mb-6">
                {t('auth.invitation.linkExpiredMessage') || 
                 'Invitation links can only be used once. If you\'ve already set your password, you can reset it below.'}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/forgot-password')}
                  className="w-full py-3 px-4 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition duration-200 flex items-center justify-center gap-2"
                >
                  {t('auth.invitation.resetPassword') || 'Reset My Password'}
                  <ArrowRightIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full py-3 px-4 border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium rounded-lg transition duration-200"
                >
                  {t('auth.invitation.backToLogin') || 'Back to Login'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 px-4 py-8">
          <div className="w-full max-w-md text-center">
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg">
              <div className="rounded-full bg-green-100 p-3 mx-auto w-fit mb-4">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">{t('auth.invitation.successTitle')}</h2>
              <p className="text-slate-600">
                {t('auth.invitation.successMessage')}
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-slate-800 mb-2">{t('auth.invitation.title')}</h1>
              <p className="text-slate-600">{t('auth.invitation.subtitle')}</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('auth.invitation.passwordLabel')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('auth.invitation.passwordPlaceholder')}
                    required
                    disabled={isSubmitting || !token}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {password && (
                  <div className="mt-3 space-y-2">
                    {passwordStrength && !isCheckingStrength && (
                      <>
                        <div className="flex gap-1">
                          {[25, 50, 75, 100].map((threshold) => (
                            <div
                              key={threshold}
                              className={`h-1.5 flex-1 rounded-full transition-colors ${
                                passwordStrength.score >= threshold 
                                  ? getStrengthBarColor(passwordStrength.strength)
                                  : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                        
                        <div className={`text-xs font-medium px-2 py-1 rounded border inline-block ${
                          getStrengthColor(passwordStrength.strength)
                        }`}>
                          {t(`auth.invitation.strength.${passwordStrength.strength}`)}
                        </div>
                      </>
                    )}

                    <div className="space-y-1 text-xs">
                      <div className={`flex items-center ${
                        meetsRequirement('length') ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        {meetsRequirement('length') ? (
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                        ) : (
                          <XCircleIcon className="h-4 w-4 mr-1" />
                        )}
                        {t('auth.invitation.requirements.minLength')}
                      </div>
                      <div className={`flex items-center ${
                        meetsRequirement('lowercase') ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        {meetsRequirement('lowercase') ? (
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                        ) : (
                          <XCircleIcon className="h-4 w-4 mr-1" />
                        )}
                        {t('auth.invitation.requirements.lowercase')}
                      </div>
                      <div className={`flex items-center ${
                        meetsRequirement('uppercase') ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        {meetsRequirement('uppercase') ? (
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                        ) : (
                          <XCircleIcon className="h-4 w-4 mr-1" />
                        )}
                        {t('auth.invitation.requirements.uppercase')}
                      </div>
                      <div className={`flex items-center ${
                        meetsRequirement('number') ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        {meetsRequirement('number') ? (
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                        ) : (
                          <XCircleIcon className="h-4 w-4 mr-1" />
                        )}
                        {t('auth.invitation.requirements.number')}
                      </div>
                      <div className={`flex items-center ${
                        meetsRequirement('special') ? 'text-green-600' : 'text-gray-400'
                      }`}>
                        {meetsRequirement('special') ? (
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                        ) : (
                          <XCircleIcon className="h-4 w-4 mr-1" />
                        )}
                        {t('auth.invitation.requirements.special')}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('auth.invitation.confirmPasswordLabel')}
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder={t('auth.invitation.confirmPasswordPlaceholder')}
                    required
                    disabled={isSubmitting || !token}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                
                {confirmPassword && (
                  <div className={`mt-1 text-xs flex items-center ${
                    password === confirmPassword ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {password === confirmPassword ? (
                      <>
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        {t('auth.invitation.passwordsMatch')}
                      </>
                    ) : (
                      <>
                        <XCircleIcon className="h-4 w-4 mr-1" />
                        {t('auth.invitation.passwordsNoMatch')}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Terms and Conditions Checkbox */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="accept-terms"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                  disabled={isSubmitting || !token}
                  required
                />
                <label htmlFor="accept-terms" className="text-sm text-slate-600">
                  {t('auth.invitation.termsAndConditions')}{' '}
                  <a
                    href={`https://simpleclass.eu/${i18n.language}/terms`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-600 hover:text-cyan-500 font-medium underline"
                  >
                    {t('auth.invitation.terms')}
                  </a>
                  {' '}{t('auth.invitation.and')}{' '}
                  <a
                    href={`https://simpleclass.eu/${i18n.language}/privacy`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-600 hover:text-cyan-500 font-medium underline"
                  >
                    {t('auth.invitation.privacyPolicy')}
                  </a>
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !token}
                className="w-full py-3 px-4 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t('auth.invitation.submitting') : t('auth.invitation.submitButton')}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default CompleteInvitationSetup;